/**
 * LiveVoice (web) — la voix d'un participant sur sa vignette, partagée par le live de l'élève et celui
 * du tuteur (weekly du 03/11, W1) : trois barres qui suivent sa piste de micro, dix fois par seconde,
 * comme l'ancienne vignette (`UsernameComponent`) et avec son calcul (`engine/live/room/voiceLevels`), sans
 * sa boucle d'images.
 *
 * C'est le seul élément qui se redessine au rythme de la voix : l'écran le pose dans la fente des
 * vignettes (`VoiceSlotContext`), et la salle ne lit jamais le niveau.
 */
import React, { useMemo } from "react"

import { VoiceBars } from "../../../../../../design-system"
import { useVoiceTrack } from "../../../../../../engine/live/room/useVoiceTrack"
import { listenVoiceLevels } from "../../../../../../engine/live/room/voiceLevels"

import { type LiveVoiceProps } from "./liveVoiceProps"

export type { LiveVoiceProps }

export function LiveVoice({ personId, size }: LiveVoiceProps) {
    const voice = useVoiceTrack(personId)
    if (voice?.track == null) return null
    return <Waveform track={voice.track} size={size} />
}

/**
 * Monté seulement avec une piste : les barres s'abonnent à ses niveaux (`listenVoiceLevels`, l'analyseur ouvert à la
 * piste et fermé avec elle), qui vont droit aux barres : la vignette ne se redessine pas dix fois par seconde. Une
 * nouvelle piste (ou un nouveau flux de la même piste), un nouvel abonnement ; rien d'autre ne le rouvre.
 */
function Waveform({ track, size }: { track: NonNullable<ReturnType<typeof useVoiceTrack>>["track"]; size?: number }) {
    const stream = track?.mediaStream
    const feed = useMemo(
        () =>
            track == null || stream == null
                ? undefined
                : (push: (levels: readonly number[]) => void) => listenVoiceLevels(track, push),
        [track, stream]
    )
    return <VoiceBars feed={feed} size={size} />
}
